import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import clsx from 'clsx'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import { usePipelineDataContextWithVariables } from '../provider/DDPDataProvider'
import { useHideIfNoDataWhen } from '../../HideIfNoData'
import { SKELETON_VARIANTS } from '../../../../constants'
import Skeleton from '../../../atoms/Skeleton'
import { useTitle } from './useTitle'

const useStyleVariant = (classNames, styles, variant) => {
  return useMemo(() => {
    if (variant === 'sm') {
      return [{
        container: classNames?.container,
        metric: clsx(classNames?.metric, 'font-size-md'),
        name: clsx(classNames?.name, 'font-size-xs')
      }, styles]
    }
    if (variant === 'md') {
      return [{
        container: classNames?.container,
        metric: classNames?.metric,
        name: clsx(classNames?.name, 'font-size-xs')
      }, {
        metric: {
          ...(styles?.metric || {}),
          fontSize: '2rem'
        },
        ...(styles || {})
      }]
    }
    if (variant === 'lg') {
      return [{
        container: classNames?.container,
        metric: classNames?.metric,
        name: clsx(classNames?.name, 'font-size-sm')
      }, {
        metric: {
          ...(styles?.metric || {}),
          fontSize: '3rem'
        },
        ...(styles || {})
      }]
    }
    if (variant === 'xl') {
      return [{
        container: classNames?.container,
        metric: classNames?.metric,
        name: clsx(classNames?.name, 'font-size-md')
      }, {
        metric: {
          ...(styles?.metric || {}),
          fontSize: '4rem'
        },
        ...(styles || {})
      }]
    }
    return [classNames, styles]
  }, [classNames, styles, variant])
}

function PipelineMetric ({
  pipeline,
  metricAccessor,
  format,
  name,
  classNames,
  styles,
  variant,
  variables,
  loadingHeight
}) {
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)
  useHideIfNoDataWhen(() => !isLoading && (data?.[metricAccessor] === undefined || data?.[metricAccessor] === null), [data, isLoading])
  const { formatter } = useFormattingContext()
  const formats = useMemo(() => [format].flat(), [format])
  const [currentFormat, setCurrentFormat] = useState(formats?.[0] ?? '')
  const hasMultipleFormats = useMemo(() => formats.length > 1, [formats])

  const metric = useMemo(() => {
    const metricValue = get(data, metricAccessor)
    return formatter(metricValue, currentFormat)
  }, [data, metricAccessor, formatter, currentFormat])
  const [nameSingle, nameMulti] = useTitle(name)
  const [_classNames, _styles] = useStyleVariant(classNames, styles, variant)
  const incrementFormat = useCallback(() => {
    if (formats.length <= 1) return
    const currentIndex = formats.indexOf(currentFormat)
    const nextIndex = (currentIndex + 1) % formats.length
    setCurrentFormat(formats[nextIndex])
  }, [currentFormat, formats])

  if (isLoading) {
    return <Skeleton variant={SKELETON_VARIANTS.rect} height={loadingHeight} />
  }

  return (
    <div
      className={_classNames?.container}
      style={_styles?.container}
      {...(hasMultipleFormats && {
        onClick: incrementFormat,
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            incrementFormat()
          }
        }
      })}
    >
      <div className={_classNames?.metric} style={_styles?.metric}>
        {metric}
      </div>
      {name ? (
        <div className={_classNames?.name} style={_styles?.name}>
          {metric === 1 ? nameSingle : nameMulti}
        </div>
      ) : null}
    </div>
  )
}

PipelineMetric.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,
  metricAccessor: PropTypes.string,
  format: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  classNames: PropTypes.shape({
    container: PropTypes.string,
    metric: PropTypes.string,
    name: PropTypes.string
  }),
  styles: PropTypes.shape({
    container: PropTypes.object,
    metric: PropTypes.object,
    name: PropTypes.object
  }),
  variant: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'none']),
  loadingHeight: PropTypes.string
}

PipelineMetric.defaultProps = {
  variant: 'lg',
  loadingHeight: '80px'
}

export default PipelineMetric
